import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import * as CryptoJS from 'crypto-js';
import { omit } from 'lodash-es';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { take } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { $b } from '../../../libs/browser_utils.js';
import { EventBus } from '../../../libs/event_publisher.js';
import { AppdataService } from '../app-data/appdata.service';
import { CommunicationInterfaceService } from '../communicationInterface/communication-interface.service';
import { MessengerService } from '../messenger/messenger.service';
import { WebsocketService } from '../websockets/websocket.service';

@Injectable({
  providedIn: 'root'
})
export class LivechatStartService {
  private appConfig = null;
  long: number;
  lat: number;
  chatMode: any;
  eventBus: any = EventBus;
  locationAvailable = false;
  options = {
    enableHighAccuracy: true,
    maximumAge: 10000,
    timeout: 5000
  };
  queryObjArray = [];
  encryptKey = 'ThisIsTheSecret';
  snackBarRef: any;
  ucid: string = $b.GetQueryString('ucid');
  private locationAccess = new BehaviorSubject<any>(false);
  // public queuePosText = new BehaviorSubject<any>(this.incomingText);

  locationAccessObservable: Observable<any> = this.locationAccess.asObservable(); // For Agent Message
  MediaSupport: any;
  isCaptchaEnabled: any;
  snackbarHorizontalPosition: any;
  snackbarVerticalPosition: any;
  areQueryParametersChanged: boolean = false;
  queryParameters: any;
  appLabels: any;

  constructor(
    private websocketService: WebsocketService,
    private snackBar: MatSnackBar,
    private messenger: MessengerService,
    private commInterface: CommunicationInterfaceService,
    private activatedRoute: ActivatedRoute,
    private route: Router,
    private appDataService: AppdataService,
    public zone: NgZone
  ) {
    this.snackbarHorizontalPosition = 'center';
    this.snackbarVerticalPosition = 'top';
    console.log('%c LivechatStartService', 'color:white; background: blue;');

    appDataService.appConfigSub.subscribe((x) => {
      if (!x) {
        return;
      }

      this.appConfig = x;
      this.appLabels = this.appDataService.getLabels();
      this.snackbarHorizontalPosition = this.appConfig?.Miscellaneous?.snackbarPosition?.horizontalPosition || 'center';
      this.snackbarVerticalPosition = this.appConfig?.Miscellaneous?.snackbarPosition?.verticalPosition || 'top';
      // this.isCaptchaEnabled = this.appConfig.isCaptchaEnabled;
      const browserPermisson = this.checkBrowser();
      if (navigator.geolocation) {
        if (this.appConfig.GeoLocation.Enable && browserPermisson === 'success') {
          this.getLocation();
        }
      } else if (this.appConfig.GeoLocation.Enable && browserPermisson === 'failed') {
        this.snackBar.open(`Geolocation is not supported by this browser.`, 'Dismiss', {
          duration: 3000,
          horizontalPosition: this.snackbarHorizontalPosition,
          verticalPosition: this.snackbarVerticalPosition,
          panelClass: ['mat-toolbar']
        });
      }

      this.chatMode = this.appConfig?.channelConfiguration?.chatmode;

      this.commInterface.logToServer('info', 'LivechatStartService', 'appConfig loaded');
    });

    this.commInterface.logToServer('info', 'LivechatStartService', 'Register');
  }

  checkBrowser() {
    let status = '';
    const browser = $b.DetectBrowser();
    console.log('Browser : ', browser);
    if (browser === 'gc' || 'ed' || 'ff' || 'oc' || 'sa' || 'op') {
      status = 'success';
    } else {
      status = 'failed';
    }

    this.commInterface.logToServer('info', 'LivechatStartService', `checkBrowser: ${browser}`);

    return status;
  }

  getBrowser() {
    const browser = $b.DetectBrowser();
    return browser;
  }

  // to handle error
  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
      this.commInterface.sendUIErrors('LivechatStartService', 'checkBrowser', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);
      this.commInterface.sendUIErrors('LivechatStartService', 'handleError', `Backend returned code ${error.status}, ` + `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError('Something bad happened; please try again later.');
  }

  checkforLivechatStart = (captchaValue: any ) => {
    this.startSession(captchaValue);
    this.commInterface.logToServer('info', 'LivechatStartService', 'checkforLivechatStart');
  };

  getLocation = () => {
    this.locationAccess.next(false);
    navigator.geolocation.getCurrentPosition(this.setLocation, this.ShowError);
    this.commInterface.logToServer('info', 'LivechatStartService', 'getLocation');
  };

  setLocation = (position: GeolocationPosition) => {
    console.log(position.coords.latitude + ',' + position.coords.longitude);

    try {
      if ((position.coords.latitude && position.coords.longitude) !== undefined) {
        this.locationAccess.next(true);
        this.locationAvailable = true;
        this.lat = position.coords.latitude;
        this.long = position.coords.longitude;
        this.commInterface.devicePermissions.location = true;
      }
    } catch (error) {
      console.error('Exception in LivechatStartService.setLocation', error);
      this.commInterface.sendUIErrors('LivechatStartService', 'setLocation', error);

      this.commInterface.devicePermissions.location = false;
    }
    this.commInterface.logToServer('info', 'LivechatStartService', 'setLocation');
  };

  ShowError = (error: GeolocationPositionError) => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        this.showSnackBar('Location cannot be accessed, Please check settings');
        break;
      case error.POSITION_UNAVAILABLE:
        this.showSnackBar('Location information is unavailable');
        break;
      case error.TIMEOUT:
        this.showSnackBar('Connection Timed Out checking for location');
        break;
      default:
        this.showSnackBar('Unknown Error Occured');
        break;
    }
    this.commInterface.devicePermissions.location = false;
  };

  checkQueryParamsChange = async () => {
    let currentQueryParams = Array.from(new URLSearchParams(window.location.search).keys()).reduce(
      (acc, val) => ({ ...acc, [val]: new URLSearchParams(window.location.search).get(val) }),
      {}
    );
    currentQueryParams = Object.fromEntries(Object.entries(currentQueryParams).sort());
    this.queryParameters = currentQueryParams;
    let previousQueryParams = $b.GetLocalStorage('queryParameters');
    if(previousQueryParams) previousQueryParams = Object.fromEntries(Object.entries(JSON.parse(previousQueryParams)).sort());

    if(currentQueryParams && previousQueryParams && (JSON.stringify(currentQueryParams) != JSON.stringify(previousQueryParams))){
      const channel = new BroadcastChannel('visual-ivr-window-channel');
      channel.postMessage({channelData: 'new-channel-opened'});
      console.log(JSON.stringify(currentQueryParams), previousQueryParams, $b.GetSessionStorage('sessionStarted'));
      if($b.GetSessionStorage('sessionStarted')) this.areQueryParametersChanged = true;
    }

    $b.SetLocalStorage('queryParameters', JSON.stringify(currentQueryParams));
    return new Promise<boolean>((resolve) => {
      resolve(true)
    })
  }

  startSession = async (captchaValue?: any) => {
    // subscribe to _activatedRoute for queryParams
    const queryParams = await this.activatedRoute.queryParams.pipe(take(1)).toPromise();

    console.log('LivechatStartService.queryParams', queryParams);
    const chatmode = this.appConfig?.channelConfiguration?.chatmode;
    const channelName = this.appLabels?.liveChatComponent?.ChannelLabels[chatmode];
    const infox = {
      pChannel: channelName,
      pIntent: queryParams.intent ?? '',
      pChatBotSid: uuidv4(),
      pUcid: queryParams.ucid ?? '',
      visualivr: '1',
      pLocation: this.lat + ',' + this.long,
      chatmode: chatmode,
    };

    // since we add intent and ucid to infox as 'pIntent' and 'pUcid' we remove here
    const extraDetails = omit({ ...queryParams }, ['intent', 'ucid']);

    const info = {
      eCustDetails: { ...infox, ...extraDetails },
      plainParams: 'true',
      chatmode: this.chatMode,
      eEnteredCaptcha: captchaValue,
      routepath: this.appConfig.routepath
    };

    console.log('LivechatStartService.StartSession', info);

    if (this.appConfig.websocket.enabled) {
      this.websocketService.startWebSocket();
    } else {
      if(this.appConfig.permissionsConfig?.enableRestriction){
        if (!(this.appConfig.permissionsConfig?.allowSessionStartWithoutMedia || 
          this.appConfig.permissionsConfig?.allowToProceedWithoutMedia) && !this.commInterface.isCaptchaEnabledFromBackend) {
          this.commInterface.permissionsObservable.subscribe((permission: any) => {
            if (permission.status) {
              this.commInterface.logToServer("livechatstart", "first",
                 "conditon")
                 
                 console.log("LivechatStartService.startSession", "first");
              const start = this.commInterface.start(info, []);
              if (start) {
                this.checkforStartSuccess();
              }
            }
          });
        } else if (!(this.appConfig.permissionsConfig?.allowSessionStartWithoutMedia || 
          this.appConfig.permissionsConfig?.allowToProceedWithoutMedia) && this.commInterface.isCaptchaEnabledFromBackend) {
          if (this.commInterface.availableInputDevices.audio.length > 0 && this.commInterface.availableInputDevices.video.length > 0) {
            
             this.commInterface.logToServer("livechatstart", "second",
                 "conditon")
                 
                 console.log("LivechatStartService.startSession", "second");
            const start = this.commInterface.start(info, []);
            if (start) {
              this.checkforStartSuccess();
            }
          }
        } else {
          
                 console.log("LivechatStartService.startSession", "third");
             this.commInterface.logToServer("livechatstart", "third",
                 "conditon")
          const start = this.commInterface.start(info, []);
          if (start) {
            this.checkforStartSuccess();
          }
        }
      }else{
        
          
             this.commInterface.logToServer("livechatstart", "fourth",
                 "conditon")
                 console.log("LivechatStartService.startSession", "fourth");
        const start = this.commInterface.start(info, []);
        if (start) {
          this.checkforStartSuccess();
        }
      }
    }

    this.checkMediaSupport();

    this.commInterface.logToServer('info', 'LivechatStartService', 'startSession');
  };

  checkforStartSuccess = () => {
    try {
      const timeoutDuration = this.appConfig?.timeoutDurations?.StartSession ? this.appConfig.timeoutDurations.StartSession : 10000;
      setTimeout(() => {
        console.log("checkforStartSuccess, Start Session Timeout: ", timeoutDuration);
        this.commInterface.logToServer('info', 'LivechatStartService', 'Session Start Timeout: ' + timeoutDuration);
        if ($b.GetSessionStorage('sessionStarted') === null) {
          this.commInterface.logToServer('info', 'LivechatStartService', 'Session Start Failed');

          this.snackBar.open(`Session Start Failed, Retrying...`, 'Dismiss', {
            duration: 3000,
            horizontalPosition: this.snackbarHorizontalPosition,
            verticalPosition: this.snackbarVerticalPosition,
            panelClass: ['mat-toolbar']
          });

          this.commInterface.end('Session Start Failed');

          const navigationExtras: NavigationExtras = {
            queryParamsHandling: 'preserve',
            preserveFragment: true,
            queryParams: {...this.queryParameters},
            state: { reload: true }
          };

          // [MS: Sep 13, '21] route to custom static page if configured
          if (this.appConfig?.Miscellaneous?.CustomStartPageUrl) {
            location.href = this.appConfig?.Miscellaneous?.CustomStartPageUrl + location.search;
          }
          // Navigate to main page if captcha is enabled
          else {
            // Navigate to thank-you page if captcha is not enabled
            if (this.appConfig?.Miscellaneous?.ThankyoupageEnabled) {
              this.zone.run(() => {
                this.route.navigate(['/thank-you'], navigationExtras);
              });
            } else {
              if (this.isCaptchaEnabled === true) {
                this.zone.run(() => {
                  this.route.navigate(['/'], navigationExtras);
                });
              } else {
                this.zone.run(() => {
                  this.route.navigate(['/'], navigationExtras);
                });
              }
            }
          }
        }
      }, timeoutDuration);

      this.commInterface.logToServer('info', 'LivechatStartService', 'checkforStartSuccess');
    } catch (e) {
      console.log('Error occured on checking start success', e);
    }
  };

  checkMediaSupport = () => {
    this.MediaSupport = JSON.parse($b.GetLocalStorage('deviceSupport'));
    console.log(this.MediaSupport);
    if (this.MediaSupport.isWebRTCSupported === false) {
      this.snackBar.open(`WebRTC is not Supported, Audio and Video call options are disabled`, 'Dismiss', {
        duration: 5000
      });
    }

    this.commInterface.logToServer('info', 'LivechatStartService', 'checkMediaSupport');
  };

  showSnackBar(msg: string) {
    this.snackBar.open(`${msg}`, 'Dismiss', {
      duration: 3000,
      horizontalPosition: this.snackbarHorizontalPosition,
      verticalPosition: this.snackbarVerticalPosition,
      panelClass: ['mat-toolbar']
    });
  }

  decryptAppConfig() {
    const decryptData = CryptoJS.AES.decrypt($b.GetLocalStorage('appconfig'), this.encryptKey);
    const obj = JSON.parse(decryptData.toString(CryptoJS.enc.Utf8));
    this.appConfig = obj;
  }
}
